// mattermost-poll-cursor-store.ts — F06 (phase-07 tests 20-24): persists the
// channel poll cursor (last seen create_at + an overlap-window set of
// recently-seen server post ids) so a restart resumes where it left off
// without re-delivering directives already written to the inbox, while
// still catching a post whose create_at ties the boundary (the "overlap
// window" — spec §7.3: "poll cursor + overlap"). `last_sync_at` is written
// ONLY after a poll attempt actually completes successfully — never on a
// failed/offline attempt (spec §7: "last_sync_at chỉ cập nhật khi sync thật
// thành công").
//
// Delivery order (test 20): the caller's `onNormalizedPost` callback — which
// writes to directive-inbox.ts — runs and is awaited for EVERY post BEFORE
// the cursor file on disk is advanced past that post, so a crash mid-poll
// never loses a post that was already inbox-written nor silently skips one
// that wasn't.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { readPagesBackToBoundary } from "./mattermost-older-pages-end.js";
import type { MattermostPost, MattermostRestClient } from "./mattermost-rest-client.js";

// Overlap window: how many of the most-recently-seen post ids to keep
// around purely for dedupe, independent of the create_at cursor — guards
// against two posts sharing the exact same create_at millisecond.
const OVERLAP_WINDOW_SIZE = 200;

export interface PollCursor {
  lastSeenCreateAt: number;
  seenPostIds: string[];
  lastSyncAt?: string;
}

function cursorPath(controlRoot: string): string {
  return join(controlRoot, "mattermost-poll-cursor.json");
}

export async function readPollCursor(controlRoot: string): Promise<PollCursor | undefined> {
  const path = cursorPath(controlRoot);
  if (!(await pathExists(path))) return undefined;
  return readJson<PollCursor>(path);
}

export async function writePollCursor(controlRoot: string, cursor: PollCursor): Promise<void> {
  await writeFileDurable(
    cursorPath(controlRoot),
    Buffer.from(`${JSON.stringify(cursor, null, 2)}\n`, "utf8"),
  );
}

export interface PollChannelSinceCursorInput {
  client: Pick<MattermostRestClient, "getPostsForChannel">;
  channelId: string;
  controlRoot: string;
  perPage: number;
  /** Called once per NEW post, oldest-first, and awaited before the cursor advances past it. */
  onNormalizedPost: (post: MattermostPost) => Promise<void>;
  /**
   * Posts older than this are never wanted (the mission's creation time): a
   * first poll with no cursor pages back only this far, not the whole history.
   */
  notBeforeMs?: number;
  /** Page cap before giving up (fail closed); MAX_POLL_PAGES unless a test lowers it. */
  maxPages?: number;
  now?: () => Date;
}

/**
 * Polls every page of a channel's posts, delivers new (non-deduped) posts
 * oldest-first to `onNormalizedPost`, persists the inbox write BEFORE
 * advancing the cursor for that post, and only stamps `lastSyncAt` after the
 * whole poll completes without throwing. Throws (does not swallow) on any
 * REST/network failure — callers decide retry policy; this module's only
 * contract is "never silently claim success."
 */
export async function pollChannelSinceCursor(input: PollChannelSinceCursorInput): Promise<void> {
  const now = input.now ?? (() => new Date());
  const existing = await readPollCursor(input.controlRoot);
  const seenIds = new Set(existing?.seenPostIds ?? []);
  let lastSeenCreateAt = existing?.lastSeenCreateAt ?? 0;

  // CXR-07: page 0 is the newest; go back until a page reaches the boundary
  // (cursor or mission start). Unable to get there (page cap, or the history
  // kept moving) ⇒ throw before delivering anything, cursor untouched.
  const boundary = Math.max(lastSeenCreateAt, input.notBeforeMs ?? 0);
  const { posts: allPosts } = await readPagesBackToBoundary({
    fetchPage: (page) => input.client.getPostsForChannel(input.channelId, page, input.perPage),
    boundaryMs: boundary,
    maxPages: input.maxPages,
  });

  // Oldest-first so callers observe causal order; overlap-window dedupe by
  // server post id takes priority over the create_at cursor so a post that
  // ties the boundary millisecond is never re-delivered.
  const ordered = [...allPosts].sort((a, b) => a.create_at - b.create_at);
  const newPostIds: string[] = [];
  for (const post of ordered) {
    if (seenIds.has(post.id)) continue;
    if (post.create_at < boundary) continue;

    await input.onNormalizedPost(post);

    seenIds.add(post.id);
    newPostIds.push(post.id);
    if (post.create_at > lastSeenCreateAt) lastSeenCreateAt = post.create_at;

    // Persist progress after EACH post (not just at the end) so a crash
    // mid-poll cannot lose track of what was already delivered.
    const windowedIds = [...seenIds].slice(-OVERLAP_WINDOW_SIZE);
    await writePollCursor(input.controlRoot, {
      lastSeenCreateAt,
      seenPostIds: windowedIds,
      lastSyncAt: existing?.lastSyncAt,
    });
  }

  // Only stamp lastSyncAt once the ENTIRE poll (all pages, all deliveries)
  // completed without throwing.
  const finalSeenIds = [...seenIds].slice(-OVERLAP_WINDOW_SIZE);
  await writePollCursor(input.controlRoot, {
    lastSeenCreateAt,
    seenPostIds: finalSeenIds,
    lastSyncAt: now().toISOString(),
  });
  void newPostIds;
}
